<template>
  <v-card class="pt-7 pb-3">
    <v-row class="ma-3">
      <v-card-text>
        <v-form ref="urlMonitorForm" v-model="formValid">
          <v-row>
            <v-col cols="12" sm="8">
              <div class="mb-4 d-flex align-center">
                <v-text-field
                  outlined
                  v-model="monitor.title"
                  dense
                  label="Titre"
                  :rules="[required]"
                  :disabled="loading"
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" sm="8">
              <div class="mb-4 d-flex align-center">
                <v-text-field
                  v-model="monitor.url"
                  outlined
                  dense
                  label="URL"
                  :rules="[required, urlRule, urlUniqueRule]"
                  :disabled="loading"
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" sm="8">
              <div class="mb-4 d-flex align-center">
                <v-autocomplete
                  outlined
                  v-model="monitor.site_id"
                  :items="siteList"
                  :menu-props="{ maxHeight: '400' }"
                  label="Site (optionnel)"
                  clearable
                  item-text="name"
                  item-value="id"
                  dense
                  :disabled="loading"
                ></v-autocomplete>
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="mb-4 d-flex align-center">
                <v-select
                  v-model="monitor.interval_minutes"
                  outlined
                  dense
                  label="Intervalle de vérification"
                  :items="intervalOptions"
                  item-text="text"
                  item-value="value"
                  :rules="[required]"
                  :disabled="loading"
                ></v-select>
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="mb-4 d-flex align-center">
                <v-switch
                  v-model="monitor.is_active"
                  label="Actif"
                  :disabled="loading"
                ></v-switch>
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="mb-4 d-flex align-center">
                <v-switch
                  v-model="monitor.teams_alert_enabled"
                  label="Alertes Teams activées"
                  :disabled="loading"
                ></v-switch>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-menu
                ref="menuNextCheckDate"
                v-model="nextCheckDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="nextCheckDate"
                    label="Date de la prochaine vérification (optionnel)"
                    :append-icon="icons.mdiCalendar"
                    outlined
                    dense
                    readonly
                    :disabled="loading"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="nextCheckDate"
                  @input="nextCheckDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu
                ref="menuNextCheckTime"
                v-model="nextCheckTimeMenu"
                :close-on-content-click="false"
                :return-value.sync="nextCheckTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="nextCheckTime"
                    label="Heure de la prochaine vérification (optionnel)"
                    :append-icon="icons.mdiClock"
                    outlined
                    dense
                    readonly
                    :disabled="loading"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-model="nextCheckTime"
                  format="24hr"
                  @click:minute="$refs.menuNextCheckTime.save(nextCheckTime)"
                ></v-time-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn
                color="primary"
                @click="saveMonitor"
                :loading="loading"
                :disabled="!formValid || loading"
              >
                {{ editing ? "Mettre à jour" : "Créer" }}
              </v-btn>
              <v-btn
                class="ml-3"
                color="secondary"
                outlined
                @click="goBack"
                :disabled="loading"
              >
                Annuler
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-row>
  </v-card>
</template>

<script>
import axios from "@axios";
import { mapGetters } from "vuex";
import { mdiCalendar, mdiClock } from "@mdi/js";

export default {
  name: "UrlMonitorForm",
  props: {
    monitorId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      formValid: false,
      loading: false,
      editing: false,
      monitor: {
        title: "",
        url: "",
        interval_minutes: 5,
        is_active: true,
        teams_alert_enabled: false,
      },
      nextCheckDate: null,
      nextCheckTime: null,
      nextCheckDateMenu: false,
      nextCheckTimeMenu: false,
      intervalOptions: [
        { text: "1 minute", value: 1 },
        { text: "5 minutes", value: 5 },
        { text: "10 minutes", value: 10 },
        { text: "15 minutes", value: 15 },
        { text: "30 minutes", value: 30 },
        { text: "1 heure", value: 60 },
        { text: "2 heures", value: 120 },
        { text: "6 heures", value: 360 },
        { text: "12 heures", value: 720 },
        { text: "24 heures", value: 1440 },
      ],
      icons: {
        mdiCalendar,
        mdiClock,
      },
    };
  },
  computed: {
    ...mapGetters("common", ["getSiteList"]),
    ...mapGetters("webPerf", ["getUrlMonitors", "getUrlMonitorUrls"]),
    siteList() {
      return this.getSiteList;
    },
    existingUrls() {
      return this.getUrlMonitorUrls;
    },
  },
  async mounted() {
    // Load site list from store
    if (this.getSiteList.length === 0) {
      await this.$store.dispatch("common/getSiteList");
    }

    // Load URL monitors for uniqueness validation
    if (this.getUrlMonitors.length === 0) {
      try {
        await this.$store.dispatch("webPerf/getUrlMonitors");
      } catch (error) {
        console.error("Error loading URL monitors for validation:", error);
      }
    }

    if (this.monitorId) {
      this.editing = true;
      this.loadMonitor();
    }
  },
  methods: {
    async loadMonitor() {
      this.loading = true;
      try {
        const response = await axios.get(`/url-monitors/${this.monitorId}`);
        this.monitor = { ...response.data };

        // Split datetime into separate date and time fields
        if (this.monitor.next_check_time) {
          this.nextCheckDate = this.monitor.next_check_time.slice(0, 10);
          this.nextCheckTime = new Date(
            this.monitor.next_check_time
          ).toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      } catch (error) {
        console.error("Error loading monitor:", error);
        this.$store.dispatch("alerts/addAlert", {
          message: "Erreur lors du chargement du monitor",
          type: "error",
        });
        this.goBack();
      } finally {
        this.loading = false;
      }
    },
    async saveMonitor() {
      if (!this.$refs.urlMonitorForm.validate()) {
        return;
      }

      this.loading = true;
      try {
        const payload = { ...this.monitor };

        // Handle optional site_id field
        if (!payload.site_id) {
          delete payload.site_id;
        }

        // Handle next_check_time field - combine date and time
        if (this.nextCheckDate && this.nextCheckTime) {
          // Combine date and time into ISO format
          const combinedDateTime = `${this.nextCheckDate} ${this.nextCheckTime}`;
          payload.next_check_time = new Date(combinedDateTime).toISOString();
        } else if (this.nextCheckDate && !this.nextCheckTime) {
          // If only date provided, set time to 00:00
          const combinedDateTime = `${this.nextCheckDate} 00:00`;
          payload.next_check_time = new Date(combinedDateTime).toISOString();
        } else if (!this.editing && !this.nextCheckDate) {
          // For new monitors without date/time, set next check time to now + interval
          const now = new Date();
          const nextCheck = new Date(
            now.getTime() + payload.interval_minutes * 60 * 1000
          );
          payload.next_check_time = nextCheck.toISOString();
        } else {
          // For updates or when no date provided, omit the field
          delete payload.next_check_time;
        }

        if (this.editing) {
          await this.$store.dispatch("webPerf/updateUrlMonitor", {
            id: this.monitorId,
            payload,
          });
          this.$store.dispatch("alerts/addAlert", {
            message: "Monitor mis à jour avec succès",
            type: "success",
          });
        } else {
          await this.$store.dispatch("webPerf/createUrlMonitor", payload);
          this.$store.dispatch("alerts/addAlert", {
            message: "Monitor créé avec succès",
            type: "success",
          });
        }
        this.goBack();
      } catch (error) {
        console.error("Error saving monitor:", error);
        this.$store.dispatch("alerts/addAlert", {
          message: `Erreur lors de la ${
            this.editing ? "mise à jour" : "création"
          } du monitor`,
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push({ name: "synthetic-monitoring-list" });
    },
    required(value) {
      return !!value || "Ce champ est requis";
    },
    urlRule(value) {
      if (!value) return true;
      const pattern = /^https?:\/\/.+/;
      return (
        pattern.test(value) ||
        "URL invalide (doit commencer par http:// ou https://)"
      );
    },
    urlUniqueRule(value) {
      if (!value) return true;

      // If editing, allow the current URL to pass validation
      if (this.editing) {
        const currentMonitor = this.getUrlMonitors.find(
          (m) => m.id === parseInt(this.monitorId)
        );
        if (currentMonitor && currentMonitor.url === value) {
          return true;
        }
      }

      // Check if URL already exists
      const urlExists = this.existingUrls.includes(value);
      return !urlExists || "Cette URL est déjà surveillée par un autre monitor";
    },
  },
};
</script>
